import React from "react";

import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { Application, StackType } from "src/domains/application/types";
import { getStatusChipProps } from "src/shell/styles/themeUtils";

import type { StackMetrics } from "../../types";

import { StackActionMenu } from "./StackActionMenu";

// ── Status badge colors ──────────────────────────────────────────────────────

const STATUS_COLORS: Record<
  string,
  { bg: string; border: string; text: string; dot: string }
> = {
  success: {
    bg: "rgba(16,185,129,0.12)",
    border: "rgba(16,185,129,0.3)",
    text: "#10b981",
    dot: "#10b981",
  },
  error: {
    bg: "rgba(239,68,68,0.12)",
    border: "rgba(239,68,68,0.3)",
    text: "#ef4444",
    dot: "#ef4444",
  },
  warning: {
    bg: "rgba(245,158,11,0.12)",
    border: "rgba(245,158,11,0.3)",
    text: "#f59e0b",
    dot: "#f59e0b",
  },
  info: {
    bg: "rgba(59,130,246,0.12)",
    border: "rgba(59,130,246,0.3)",
    text: "#60a5fa",
    dot: "#3b82f6",
  },
  default: {
    bg: "rgba(148,163,184,0.12)",
    border: "rgba(148,163,184,0.25)",
    text: "#94A3B8",
    dot: "#64748B",
  },
};

function StatusBadge({ status }: { status: string }) {
  const sp = getStatusChipProps(status);
  const colors = STATUS_COLORS[sp.color] ?? STATUS_COLORS.default;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 22,
        px: 1,
        borderRadius: "11px",
        bgcolor: colors.bg,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Box
        sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: colors.dot }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.text,
          lineHeight: 1,
        }}
      >
        {sp.label}
      </Typography>
    </Box>
  );
}

// ── Deploy mode badge ─────────────────────────────────────────────────────────

const DEPLOY_MODE_LABELS: Record<string, { label: string }> = {
  rusha_managed: { label: "Rusha Managed" },
  ci_build_rusha_deploy: { label: "CI Build → Rusha" },
  ci_managed: { label: "CI Managed" },
};

function DeployModeBadge({ deployMode }: { deployMode?: string }) {
  if (!deployMode) return null;
  const dm = DEPLOY_MODE_LABELS[deployMode] ?? { label: deployMode };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        px: 1.25,
        borderRadius: "11px",
        bgcolor: "rgba(59,130,246,0.1)",
        border: "1px solid rgba(59,130,246,0.25)",
      }}
    >
      <Typography
        sx={{ fontSize: 12, fontWeight: 600, color: "#60a5fa", lineHeight: 1 }}
      >
        {dm.label}
      </Typography>
    </Box>
  );
}

// ── Stack type icon ───────────────────────────────────────────────────────────

function typeIcon(systemType: StackType | undefined) {
  // Use first letter of the stack type as a simple icon
  if (systemType === StackType.DATA_ENGINEERING) return "D";
  if (systemType === StackType.ANALYTICAL) return "A";
  return "S";
}

// ── Primary action logic ──────────────────────────────────────────────────────

interface PrimaryAction {
  label: string;
  icon: React.ReactElement;
  variant: "contained" | "outlined";
  color: "primary" | "success" | "warning" | "info" | "error";
  disabled: boolean;
  tooltip?: React.ReactNode;
  onClick: () => void;
}

function getPrimaryAction(
  system: Application,
  validation: {
    canDeploy: boolean;
    canResume: boolean;
    isCiControlled: boolean;
    isCiManaged: boolean;
    isCiBuildRushaDeploy: boolean;
    isRushaBuildCiDeploy: boolean;
    needsConfigure: boolean;
    configReady: boolean;
    configValidations: {
      hasConfig: boolean;
      hasVcsRepo: boolean;
      notDeleted: boolean;
      billingReady: boolean;
    };
    isProvisioning: boolean;
  },
  actionLoading: boolean,
  actions: {
    onDeploy: () => void;
    onResume: () => void;
    onBuildAndDeploy: () => void;
    onBuildOnly: () => void;
    onEdit: () => void;
  },
  isAdopted?: boolean,
): PrimaryAction | null {
  if (validation.isCiControlled) return null;

  if (validation.canResume) {
    return {
      label: "Resume",
      icon: actionLoading ? <CircularProgress size={14} /> : <PlayArrowIcon />,
      variant: "outlined",
      color: "success",
      disabled: actionLoading,
      onClick: actions.onResume,
    };
  }

  if (validation.needsConfigure) {
    return {
      label: isAdopted ? "Review Services" : "Configure",
      icon: <EditIcon />,
      variant: "contained",
      color: "primary",
      disabled: false,
      onClick: actions.onEdit,
    };
  }

  if (validation.isRushaBuildCiDeploy && validation.canDeploy) {
    return {
      label: "Build Only",
      icon: actionLoading ? (
        <CircularProgress size={14} color="inherit" />
      ) : (
        <PlayArrowIcon />
      ),
      variant: "outlined",
      color: "primary",
      disabled: actionLoading,
      onClick: actions.onBuildOnly,
    };
  }

  if (validation.canDeploy) {
    return {
      label: "Run Pipeline",
      icon: actionLoading ? (
        <CircularProgress size={14} color="inherit" />
      ) : (
        <PlayArrowIcon />
      ),
      variant: "contained",
      color: "primary",
      disabled: actionLoading,
      onClick: actions.onBuildAndDeploy,
    };
  }

  const tooltipBlocks: string[] = [];
  if (!validation.configValidations.hasConfig)
    tooltipBlocks.push("Stack configuration");
  if (!validation.configValidations.hasVcsRepo && !validation.isProvisioning)
    tooltipBlocks.push("VCS repository connection");
  if (!validation.configValidations.notDeleted)
    tooltipBlocks.push("Stack is deleted");
  if (!validation.configValidations.billingReady)
    tooltipBlocks.push("Billing quota exhausted");

  const tooltipContent = validation.isProvisioning ? (
    <Typography variant="caption" fontWeight={600}>
      Waiting for provisioning — check the Pipeline tab for progress.
    </Typography>
  ) : !validation.configValidations.billingReady ? (
    <Typography variant="caption" fontWeight={600}>
      Free tier stack limit reached — activate paid billing to deploy more
      stacks.
    </Typography>
  ) : (
    <>
      <Typography variant="caption" fontWeight={600}>
        Cannot deploy — requires:
      </Typography>
      <ul style={{ margin: "4px 0", paddingLeft: 16, fontSize: 11 }}>
        {tooltipBlocks.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
    </>
  );

  return {
    label: "Deploy",
    icon: <RocketLaunchIcon />,
    variant: "contained",
    color: "primary",
    disabled: true,
    tooltip: tooltipContent,
    onClick: () => {},
  };
}

// ── Main component ────────────────────────────────────────────────────────────

interface StackHeaderProps {
  system: Application;
  metrics: StackMetrics | null;
  actionLoading: boolean;
  canDeploy: boolean;
  canPause: boolean;
  canResume: boolean;
  isCiControlled: boolean;
  isCiManaged: boolean;
  isCiBuildRushaDeploy: boolean;
  isRushaBuildCiDeploy: boolean;
  isRushaOwnedBuild: boolean;
  needsConfigure: boolean;
  configReady: boolean;
  configValidations: {
    hasConfig: boolean;
    hasVcsRepo: boolean;
    notDeleted: boolean;
    billingReady: boolean;
  };
  isProvisioning: boolean;
  isAdopted?: boolean;
  onDeploy: () => void;
  onPause: () => void;
  onResume: () => void;
  onProvisionVcs: () => void;
  onBuildAndDeploy: () => void;
  onBuildOnly: () => void;
  onEdit: () => void;
  onDelete: () => void;
  promotionTargets?: { slug: string; name: string }[];
  onPromote?: (targetSlug: string) => void;
  promoteLoading?: boolean;
}

export function StackHeader({
  system,
  actionLoading,
  canDeploy,
  canPause,
  canResume,
  isCiControlled,
  isCiManaged,
  isCiBuildRushaDeploy,
  isRushaBuildCiDeploy,
  needsConfigure,
  configReady,
  configValidations,
  isProvisioning,
  isAdopted,
  onDeploy,
  onPause,
  onResume,
  onProvisionVcs,
  onBuildAndDeploy,
  onBuildOnly,
  onEdit,
  onDelete,
  promotionTargets,
  onPromote,
  promoteLoading,
}: StackHeaderProps) {
  const primaryAction = getPrimaryAction(
    system,
    {
      canDeploy,
      canResume,
      isCiControlled,
      isCiManaged,
      isCiBuildRushaDeploy,
      isRushaBuildCiDeploy,
      needsConfigure,
      configReady,
      configValidations,
      isProvisioning,
    },
    actionLoading,
    { onDeploy, onResume, onBuildAndDeploy, onBuildOnly, onEdit },
    isAdopted,
  );

  const needsVcsProvisioning = !system.vcsRepoUrl;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        {/* Compact icon (28×28) */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(59,130,246,0.12)",
            color: "primary.main",
            flexShrink: 0,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {typeIcon(system.systemType)}
        </Box>

        {/* Stack name */}
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: 22 }}>
          {system.name}
        </Typography>

        {/* Status badge */}
        <StatusBadge status={system.status} />

        {/* Deploy mode badge */}
        <DeployModeBadge deployMode={system.deployMode} />

        {/* Repository link chip */}
        {system.vcsRepoUrl && (
          <Link
            href={system.vcsRepoUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              height: 22,
              px: 1.25,
              borderRadius: "11px",
              bgcolor: "rgba(148,163,184,0.1)",
              border: "1px solid rgba(148,163,184,0.2)",
              textDecoration: "none",
              color: "text.secondary",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1,
              transition: "background 150ms ease",
              "&:hover": {
                bgcolor: "rgba(148,163,184,0.16)",
              },
            }}
          >
            {system.vcsRepoUrl
              .replace(/^https?:\/\//, "")
              .replace(/\.git$/, "")
              .replace(/^github\.com\//, "")}
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        )}

        {/* Delete protection lock */}
        {system.deleteProtection && (
          <Tooltip title="Delete protection enabled">
            <LockIcon fontSize="small" sx={{ color: "warning.main" }} />
          </Tooltip>
        )}

        {/* CI-controlled chip */}
        {isCiControlled && (
          <Tooltip
            title={
              isCiManaged
                ? "CI manages the full pipeline. Manual deploy is disabled."
                : "CI builds, then triggers Rusha deploy automatically."
            }
            arrow
            placement="bottom"
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: 22,
                px: 1.25,
                borderRadius: "11px",
                bgcolor: "rgba(59,130,246,0.08)",
                border: "1px solid rgba(59,130,246,0.2)",
                fontSize: 12,
                fontWeight: 600,
                color: "#60a5fa",
                gap: 0.5,
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 14 }} />
              {isCiManaged ? "CI Managed" : "CI → Rusha"}
            </Box>
          </Tooltip>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Right side: primary action + overflow menu */}
        <Stack direction="row" spacing={1} alignItems="center">
          {primaryAction &&
            (primaryAction.tooltip ? (
              <Tooltip title={primaryAction.tooltip} arrow placement="bottom">
                <span>
                  <Button
                    variant={primaryAction.variant}
                    size="small"
                    color={primaryAction.color}
                    startIcon={primaryAction.icon}
                    disabled
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      opacity: 0.6,
                    }}
                  >
                    {primaryAction.label}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant={primaryAction.variant}
                size="small"
                color={primaryAction.color}
                startIcon={primaryAction.icon}
                disabled={primaryAction.disabled}
                onClick={primaryAction.onClick}
                sx={{ borderRadius: 1.5, textTransform: "none" }}
              >
                {primaryAction.label}
              </Button>
            ))}

          <StackActionMenu
            canPause={canPause}
            canResume={canResume}
            canDeploy={
              canDeploy &&
              !canResume &&
              !needsConfigure &&
              primaryAction?.label === "Run Pipeline"
            }
            hasVcsRepo={Boolean(system.vcsRepoUrl)}
            vcsRepoUrl={system.vcsRepoUrl}
            actionLoading={actionLoading}
            needsVcsProvisioning={needsVcsProvisioning}
            onEdit={onEdit}
            onDelete={onDelete}
            onPause={onPause}
            onResume={onResume}
            onProvisionVcs={onProvisionVcs}
            onDeploy={onDeploy}
            promotionTargets={promotionTargets}
            onPromote={onPromote}
            promoteLoading={promoteLoading}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

export default StackHeader;
